import { Divider, Stack, Typography } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useSwrFetch } from "hooks";

import { Answer } from "./components/Answer";
import { QuestionAuthor } from "./components/Authors";
import { SubmitQuestionMenu } from "./components/SubmitQuestionMenu";

import type { ShipmentQACarrierView } from "types/shipments/qa";

interface Props {
  shipment: CarrierCondensedShipment | undefined;
}

export function CarrierShipmentQATab(props: Props) {
  const { shipment } = props;

  const url = shipment != null ? `/shipments/${shipment.info.id}/qa` : null;

  const { data, loading, error } = useSwrFetch<ShipmentQACarrierView[]>({
    url,
    errorMessage: "Failed to fetch shipment Q&A",
  });

  if (shipment === undefined) {
    return null;
  }

  if (error != null) {
    return <ErrorTypography error={error} />;
  }

  const qaList = data ?? [];

  if (qaList.length < 1) {
    return (
      <LoadingWrappedNode loading={loading}>
        <Stack>
          <SubmitQuestionMenu
            shipmentId={shipment.info.id}
            shipment={shipment}
          />
          <Typography mt=".5rem" align="center">
            No questions have been asked for this shipment.
          </Typography>
        </Stack>
      </LoadingWrappedNode>
    );
  }

  return (
    <LoadingWrappedNode loading={loading}>
      <Stack direction="column" justifyContent="space-between">
        <SubmitQuestionMenu shipmentId={shipment.info.id} shipment={shipment} />
        <Stack divider={<Divider />} spacing={1}>
          {qaList.map((shipmentQA) => (
            <Stack direction="column" spacing={1} key={shipmentQA.question.id}>
              <QuestionAuthor
                questionId={shipmentQA.question.id}
                timestamp={shipmentQA.question.created_at}
                userInfo={shipmentQA.question.user_info}
              />
              <Typography variant="h5">
                {shipmentQA?.question?.text ?? "-"}
              </Typography>
              <Answer
                answer={shipmentQA.answer}
                isPartnerShipment={!!shipment?.is_partner_shipment}
              />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </LoadingWrappedNode>
  );
}
